import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  getInvestorDashboardSummary,
  getInvestorMyPools,
  getInvestorPoolDetails
} from '../lib/api';
import {
  TrendingUp,
  DollarSign,
  PieChart,
  LogOut,
  Building2,
  Wallet,
  Clock,
  CheckCircle2,
  ArrowRight,
  Truck,
  FileText,
  Calendar,
  RefreshCw,
  ChevronRight,
  User,
  Download
} from 'lucide-react';

// Create an investor-specific axios instance
const getInvestorToken = () => localStorage.getItem('investor_token');

const InvestorPortalPage = () => {
  const navigate = useNavigate();
  const [investor, setInvestor] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [myPools, setMyPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolDetails, setPoolDetails] = useState(null);
  const [showPoolDialog, setShowPoolDialog] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Load investor from localStorage
  useEffect(() => {
    const storedInvestor = localStorage.getItem('investor');
    const token = getInvestorToken();
    if (!storedInvestor || !token) {
      toast.error('Please login to access the portal');
      navigate('/investor-login');
      return;
    }
    setInvestor(JSON.parse(storedInvestor));
  }, [navigate]);

  // Logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem('investor_token');
    localStorage.removeItem('investor');
    navigate('/investor-login');
  }, [navigate]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, poolsRes] = await Promise.all([
        getInvestorDashboardSummary(),
        getInvestorMyPools()
      ]);
      setDashboardData(dashboardRes.data);
      setMyPools(poolsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (investor) {
      fetchDashboard();
    }
  }, [investor, fetchDashboard]);

  // View pool details
  const handleViewPool = async (pool) => {
    setSelectedPool(pool);
    setShowPoolDialog(true);
    setLoadingDetails(true);
    try {
      const response = await getInvestorPoolDetails(pool.pool_id);
      setPoolDetails(response.data);
    } catch (error) {
      toast.error('Failed to load pool details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading || !investor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  const summary = dashboardData?.summary || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-emerald-800">Investor Portal</h1>
                <p className="text-xs text-muted-foreground">Nexus Investment Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
                <User className="h-4 w-4" />
                <span data-testid="investor-name">{investor?.name}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 hover:text-red-600 hover:border-red-200"
                data-testid="investor-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800" data-testid="investor-welcome">
            Welcome back, {investor?.name?.split(' ')[0]}!
          </h2>
          <p className="text-muted-foreground mt-1">Here's an overview of your investment portfolio</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/80 backdrop-blur-sm border-emerald-100" data-testid="total-invested-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Invested</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">
                    {formatCurrency(summary.total_invested)}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Wallet className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-blue-100" data-testid="roi-recovered-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">ROI Recovered</p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">
                    {formatCurrency(summary.total_roi_recovered)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-purple-100" data-testid="profit-received-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Profit Received</p>
                  <p className="text-2xl font-bold text-purple-700 mt-1">
                    {formatCurrency(summary.total_profit_received)}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-xl">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-orange-100" data-testid="active-pools-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Pools</p>
                  <p className="text-2xl font-bold text-orange-700 mt-1">
                    {summary.active_pools || 0}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <PieChart className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Total Returns Highlight */}
        <Card className="mb-8 bg-gradient-to-r from-emerald-600 to-teal-600 text-white" data-testid="total-returns-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-emerald-100 font-medium">Total Returns (ROI + Profit)</p>
                <p className="text-4xl font-bold mt-1">
                  {formatCurrency(summary.total_returns)}
                </p>
              </div>
              <div className="flex items-center gap-2 text-emerald-100">
                <CheckCircle2 className="h-5 w-5" />
                <span>All payouts up to date</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Investment Pools */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-800">My Investment Pools</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboard}
              className="text-slate-600"
              data-testid="refresh-pools-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {myPools.length === 0 ? (
            <Card className="bg-white/80">
              <CardContent className="p-12 text-center">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">You don't have any active investments yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {myPools.map((pool) => (
                <Card 
                  key={pool.pool_id} 
                  className="bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow cursor-pointer"
                  data-testid={`pool-card-${pool.pool_id}`}
                  onClick={() => handleViewPool(pool)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                      {/* Pool Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-bold text-slate-800">{pool.pool_name}</h4>
                          <Badge 
                            variant={pool.pool_status === 'active' ? 'default' : 'secondary'}
                            className={pool.pool_status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}
                          >
                            {pool.pool_status}
                          </Badge>
                          {pool.investment.is_profit_stage && (
                            <Badge className="bg-purple-100 text-purple-700">Profit Stage</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">{pool.pool_description || 'Investment pool'}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Investment</span>
                            <p className="font-semibold text-slate-700">{formatCurrency(pool.investment.amount)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Ownership</span>
                            <p className="font-semibold text-slate-700">{pool.investment.ownership_percentage?.toFixed(2)}%</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">ROI Recovered</span>
                            <p className="font-semibold text-blue-600">{formatCurrency(pool.investment.roi_recovered)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Profit Received</span>
                            <p className="font-semibold text-purple-600">{formatCurrency(pool.investment.profit_received)}</p>
                          </div>
                        </div>
                      </div>

                      {/* ROI Progress */}
                      <div className="lg:w-64">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">ROI Progress</span>
                          <span className="font-semibold text-emerald-600">
                            {pool.investment.roi_progress_percent?.toFixed(1)}%
                          </span>
                        </div>
                        <Progress 
                          value={pool.investment.roi_progress_percent || 0} 
                          className="h-3 bg-slate-100"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>{formatCurrency(pool.investment.roi_recovered)}</span>
                          <span>{formatCurrency(pool.investment.roi_target)}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full mt-3 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Payouts */}
        {dashboardData?.recent_payouts?.length > 0 && (
          <div>
            <h3 className="text-xl font-bold text-slate-800 mb-4">Recent Payouts</h3>
            <Card className="bg-white/80">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Pool</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.recent_payouts.map((payout, idx) => (
                      <TableRow key={payout.id || idx}>
                        <TableCell>{formatDate(payout.payout_date)}</TableCell>
                        <TableCell className="font-medium">{payout.pool_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {payout.payout_type || 'roi'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {formatCurrency(payout.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Pool Details Dialog */}
      <Dialog open={showPoolDialog} onOpenChange={setShowPoolDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" />
              {selectedPool?.pool_name}
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="py-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="text-muted-foreground">Loading pool details...</p>
            </div>
          ) : poolDetails ? (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="assets">Assets</TabsTrigger>
                <TabsTrigger value="payouts">Payout History</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-4">
                {/* ROI Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">ROI Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <span className="text-xs text-muted-foreground">Target ROI</span>
                        <p className="text-lg font-bold text-slate-700">{formatCurrency(poolDetails.roi_summary?.target)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Recovered</span>
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(poolDetails.roi_summary?.recovered)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Remaining</span>
                        <p className="text-lg font-bold text-orange-600">{formatCurrency(poolDetails.roi_summary?.remaining)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Profit Received</span>
                        <p className="text-lg font-bold text-purple-600">{formatCurrency(poolDetails.roi_summary?.profit_received)}</p>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{poolDetails.roi_summary?.progress_percent?.toFixed(1)}%</span>
                      </div>
                      <Progress value={poolDetails.roi_summary?.progress_percent || 0} className="h-3" />
                    </div>
                    {poolDetails.roi_summary?.is_profit_stage && (
                      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-center gap-2 text-purple-700">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">ROI fully recovered! Now in profit-sharing stage.</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Profit Share Rules */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Profit Sharing Rules</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <span className="text-xs text-muted-foreground">Investor Share (Rental)</span>
                        <p className="text-2xl font-bold text-emerald-600">
                          {poolDetails.profit_share_rules?.investor_percent || 45}%
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <span className="text-xs text-muted-foreground">Investor Share (Docs)</span>
                        <p className="text-2xl font-bold text-blue-600">
                          {poolDetails.profit_share_rules?.document_investor_percent || 50}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Investment Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Your Investment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-xs text-muted-foreground">Amount Invested</span>
                        <p className="text-lg font-bold">{formatCurrency(poolDetails.investment?.investment_amount)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Investment Date</span>
                        <p className="text-lg font-bold">{formatDate(poolDetails.investment?.investment_date)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Ownership Percentage</span>
                        <p className="text-lg font-bold">{poolDetails.investment?.ownership_percentage?.toFixed(2)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Assets Tab */}
              <TabsContent value="assets" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Pool Assets ({poolDetails.assets?.length || 0})
                    </CardTitle>
                    <CardDescription>Equipment deployed under this investment pool</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {poolDetails.assets?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No assets assigned to this pool yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {poolDetails.assets?.map((asset) => (
                          <div 
                            key={asset.id} 
                            className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-slate-800">{asset.asset_type}</p>
                                <p className="text-sm text-muted-foreground">{asset.plate_number || asset.serial_number}</p>
                              </div>
                              <Badge variant={asset.status === 'deployed' ? 'default' : 'secondary'}>
                                {asset.status}
                              </Badge>
                            </div>
                            {asset.deployment && (
                              <div className="mt-3 p-3 bg-emerald-50 rounded-lg text-sm">
                                <div className="flex items-center gap-2 text-emerald-700">
                                  <Building2 className="h-4 w-4" />
                                  <span className="font-medium">Deployed to: {asset.deployment.client_name}</span>
                                </div>
                                {asset.deployment.site_location && (
                                  <p className="text-emerald-600 ml-6">Site: {asset.deployment.site_location}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payouts Tab */}
              <TabsContent value="payouts" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Payout History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {poolDetails.payout_history?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No payouts recorded yet.</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Period</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {poolDetails.payout_history?.map((payout, idx) => (
                            <TableRow key={payout.id || idx}>
                              <TableCell>{formatDate(payout.payout_date)}</TableCell>
                              <TableCell>{payout.period || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">{payout.payout_type || 'roi'}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-emerald-600">
                                {formatCurrency(payout.amount)}
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  payout.status === 'paid' 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }>
                                  {payout.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <p className="text-muted-foreground text-center py-8">Failed to load pool details.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-emerald-100 bg-white/60 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <p>Nexus Investor Portal - Secure Investment Management</p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorPortalPage;
